import { Button, Checkbox, DatePicker, Drawer, Form, Input, Select, Row, Col, Modal } from 'antd';
import moment from 'moment';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getUserEmailAddress, getUserIsMODAdmin, getUserPositions } from '../../store/user/user-reducer';
import {
  fetchLocationsForRequestor,
  fetchRequestors,
  SaveHooAccommodation,
  fetchUnplannedLocations,
  fetchRequestReasons,
  fetchRequestTypes,
} from './actions';
import {
  getAllRequestorData,
  getHoursOptions,
  getLocations,
  getReasons,
  getRequestors,
  getRequestReasons,
  getRequestTypes,
  getUnplannedLocations,
} from './selectors';
import LocationPickerPopup from './LocationPickerPopup';
import { api, showErrorNotification } from '@med-fe/util';

interface AddHooRequestDrawerProps {
  open: boolean;
  onClose: () => void;
  onSubmit?: (values: any) => void;
  loading?: boolean;
  modalData?: any;
  hooType?: 'StandardHoo' | 'UnPlannedHoo';
}

const { Option } = Select;
const { TextArea } = Input;

const dayFields = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const DELAYED_OPENING = 'delayed opening';
const EARLY_CLOSURE = 'early closure';
const CLOSED_OPTION = 'CLOSED';

const isSingleTimeRequestType = (requestType: any): boolean =>
  typeof requestType === 'string' && [DELAYED_OPENING, EARLY_CLOSURE].includes(requestType.toLowerCase());

const isDelayedOpeningRequestType = (requestType: any): boolean =>
  typeof requestType === 'string' && requestType.toLowerCase() === DELAYED_OPENING;

// Delayed Opening / Early Closure: 7:00 AM to 9:00 PM in 30 minute steps, plus CLOSED
const singleTimeOptions: string[] = (() => {
  const options: string[] = [];
  for (let minutes = 7 * 60; minutes <= 21 * 60; minutes += 30) {
    const hour24 = Math.floor(minutes / 60);
    const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
    const meridiem = hour24 < 12 ? 'AM' : 'PM';
    options.push(`${hour12}:${(minutes % 60).toString().padStart(2, '0')} ${meridiem}`);
  }
  options.push(CLOSED_OPTION);
  return options;
})();

const convertTo24Hour = (timeStr: string): string => {
  const [time, meridiem] = timeStr.trim().split(' ');
  let [hour, minute] = time.split(':').map(Number);

  if (meridiem.toUpperCase() === 'PM' && hour < 12) hour += 12;
  if (meridiem.toUpperCase() === 'AM' && hour === 12) hour = 0;

  return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
};

const AddHooRequestDrawer: React.FC<AddHooRequestDrawerProps> = ({
  open,
  onClose,
  onSubmit,
  loading = false,
  modalData = null,
  hooType = 'StandardHoo',
}) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();

  const requestors = useSelector(getRequestors);
  const locations = useSelector(getLocations);
  const reasons = useSelector(getReasons);
  const hourOptions = useSelector(getHoursOptions);
  const allRequestorData = useSelector(getAllRequestorData);
  const userEmailAddress = useSelector(getUserEmailAddress);
  const requestReasons = useSelector(getRequestReasons);
  const requestTypes = useSelector(getRequestTypes);
  const userPositions = useSelector(getUserPositions);
  const userIsMODAdmin = useSelector(getUserIsMODAdmin);
  const [templateImpactChecked, setTemplateImpactChecked] = React.useState(false);
  // Track the currently selected requestor's ContactSid for UnPlanned HOO
  const [selectedRequestorId, setSelectedRequestorId] = React.useState<number | null>(null);
  const [callForwardingUnavailableDestinationLocationIds, setCallForwardingUnavailableDestinationLocationIds] =
    React.useState<number[]>([]);
  const [callForwardingUnavailableSourceLocationIds, setCallForwardingUnavailableSourceLocationIds] = React.useState<
    number[]
  >([]);
  const locationLabelCacheRef = React.useRef<Map<number, string>>(new Map());

  // Compute filtered positions to pass as query param for UnPlannedHoo API calls.
  // If MOD admin, or user holds any higher position (RD, RCD, RCFD, AV, RM), treat as admin — no filter needed.
  // Only pass positions (e.g. 'DM', 'AM') when user is not admin and has none of the higher positions.
  const higherPositions = ['RD', 'RCD', 'RCFD', 'AV', 'RM'];
  const hasHigherPosition = Array.isArray(userPositions) && userPositions.some((pos) => higherPositions.includes(pos));

  // DM or AM user: is DM or AM, not admin, and has no higher positions (RD, RCD, RCFD, AV, RM)
  const isDMOrAMOnly =
    hooType === 'UnPlannedHoo' &&
    !userIsMODAdmin &&
    !hasHigherPosition &&
    Array.isArray(userPositions) &&
    (userPositions.includes('DM') || userPositions.includes('AM'));

  // DM user (not admin, no higher positions) may raise UnPlanned HOO requests on behalf of
  // another DM who shares at least one region with them. AM-only users stay locked to themselves.
  const isDMOnly = isDMOrAMOnly && userPositions.includes('DM');

  const loggedInContactSid: number | null = React.useMemo(() => {
    if (!userEmailAddress || !Array.isArray(allRequestorData)) return null;
    const match = allRequestorData.find(
      (item: any) => (item.ContactEmail || '').toLowerCase() === userEmailAddress.toLowerCase()
    );
    return match?.ContactSid ?? null;
  }, [allRequestorData, userEmailAddress]);

  const isActingOnBehalfOfAnotherDM =
    isDMOnly && selectedRequestorId != null && loggedInContactSid != null && selectedRequestorId !== loggedInContactSid;

  // For a DM user: self + DMs sharing at least one region with the logged-in DM's offices.
  // Null when the logged-in DM's contact is not (yet) resolved.
  const sameRegionDMContactSids: Set<number> | null = React.useMemo(() => {
    if (!isDMOnly || loggedInContactSid == null || !Array.isArray(allRequestorData)) return null;
    const isDMRow = (item: any) => (item.JobPosition || item.Position || '') === 'DM';
    // Same region-id resolution order as LocationPickerPopup
    const regionIdOf = (item: any) => item.RegionSid || item.RegionId || item.regionId;
    const myRegionIds = new Set<number>(
      allRequestorData
        .filter((item: any) => item.ContactSid === loggedInContactSid && isDMRow(item) && regionIdOf(item))
        .map((item: any) => Number(regionIdOf(item)))
    );
    const allowed = new Set<number>([loggedInContactSid]);
    allRequestorData.forEach((item: any) => {
      const regionId = regionIdOf(item);
      if (isDMRow(item) && regionId && myRegionIds.has(Number(regionId))) {
        allowed.add(item.ContactSid);
      }
    });
    return allowed;
  }, [isDMOnly, loggedInContactSid, allRequestorData]);

  const requestorOptions = React.useMemo(() => {
    if (!Array.isArray(requestors)) return requestors;
    // Editing (requestor field is locked): keep the full list so the existing requestor's name always displays
    if (!isDMOnly || modalData != null) return requestors;
    // DM user: show nothing until the allowed same-region DM set is resolved
    if (!sameRegionDMContactSids) return [];
    return requestors.filter((r: any) => sameRegionDMContactSids.has(r.value));
  }, [requestors, isDMOnly, modalData, sameRegionDMContactSids]);

  // For DM/AM users: filter locations by the logged-in user's DM/AM position.
  // When a DM acts on behalf of another DM, don't filter by the logged-in user's own associations;
  // locations are restricted to the selected DM's offices instead.
  // For RVP/Admin users: when they select a different requestor, filter locations by that requestor
  const filteredPositions: string =
    hooType === 'UnPlannedHoo' &&
    !userIsMODAdmin &&
    !hasHigherPosition &&
    Array.isArray(userPositions) &&
    !isActingOnBehalfOfAnotherDM
      ? userPositions.filter((pos) => ['DM', 'AM'].includes(pos)).join(',')
      : '';

  // Helper function to determine if requestor should show all locations (UnPlanned HOO only)
  // For Standard HOO: always filter by requestor (return false)
  // For UnPlanned HOO:
  //   - If selected requestor is DM or AM → show filtered locations for that requestor (return false)
  //   - For all other requestors (AV, RM, etc.) → show all locations (return true)
  const shouldShowAllLocations = (contactSid: number): boolean => {
    // Standard HOO always filters by requestor (original behavior)
    if (hooType !== 'UnPlannedHoo') {
      return false;
    }

    // DM users only ever see the selected requestor's (self or same-region DM) own offices
    if (isDMOnly) {
      return false;
    }

    // UnPlanned HOO: check requestor's position
    const requestorData = allRequestorData.find((item: any) => item.ContactSid === contactSid);
    if (requestorData) {
      const position = requestorData.Position || requestorData.JobPosition || '';
      // DM and AM requestors should have filtered locations
      if (['DM', 'AM'].includes(position)) {
        return false;
      }
    }
    // All other requestors (AV, RM, etc.) or no position found → show all locations
    return true;
  };

  const handleFormClose = (isSubmit?: boolean) => {
    form.resetFields();
    setTemplateImpactChecked(false);
    setSelectedRequestorId(null);
    setCallForwardingUnavailableDestinationLocationIds([]);
    setCallForwardingUnavailableSourceLocationIds([]);
    setLocationPopupSelection([]);
    setPopupLocationObjects([]);
    if (!isSubmit && onClose) {
      onClose();
    } else if (isSubmit && onSubmit) {
      onSubmit('');
    }
  };
  // Watch date values for visibleDays calculation
  const startDateValue = Form.useWatch('startDate', form);
  const endDateValue = Form.useWatch('endDate', form);

  const parseIdList = (value: any): number[] => {
    if (!Array.isArray(value)) {
      return [];
    }

    return value.map((id: any) => Number(id)).filter((id: number) => Number.isFinite(id) && id > 0);
  };

  const normalizeNumericLocationIds = (value: any): number[] => {
    const rawValues = Array.isArray(value) ? value : value != null && value !== '' ? [value] : [];

    return rawValues.map((id: any) => Number(id)).filter((id: number) => Number.isFinite(id) && id > 0);
  };

  const rememberLocationLabels = (items: any[]) => {
    if (!Array.isArray(items) || items.length === 0) {
      return;
    }

    const cache = locationLabelCacheRef.current;
    items.forEach((item: any) => {
      const locationId = Number(item?.value ?? item?.id ?? item?.Id ?? item?.LocationSid ?? item?.LocationId);
      const label =
        typeof item?.label === 'string' && item.label.trim().length > 0
          ? item.label
          : item?.LocationNumber && item?.LocationName
          ? `${item.LocationNumber} - ${item.LocationName}`
          : undefined;

      if (Number.isFinite(locationId) && locationId > 0 && label) {
        cache.set(locationId, label);
      }
    });
  };

  const fetchCallForwardingValidation = async (
    startDate: string,
    endDate: string,
    sourceLocationIds: number[],
    destinationLocationId?: number
  ) => {
    try {
      const sourceIdsParam = sourceLocationIds.join(',');
      const destinationParam = destinationLocationId ? `&destinationLocationId=${destinationLocationId}` : '';
      const endpoint = `/location-call-forwarding-requests/validation?startDate=${startDate}&endDate=${endDate}&sourceLocationIds=${sourceIdsParam}${destinationParam}`;
      const response: any = await api.get(endpoint, true, true);

      return {
        isValid: Boolean(response?.isValid ?? response?.IsValid),
        unavailableAsDestinationLocationIds: parseIdList(
          response?.unavailableAsDestinationLocationIds ?? response?.UnavailableAsDestinationLocationIds
        ),
        unavailableAsSourceLocationIds: parseIdList(
          response?.unavailableAsSourceLocationIds ?? response?.UnavailableAsSourceLocationIds
        ),
        invalidSelectedSourceLocationIds: parseIdList(
          response?.invalidSelectedSourceLocationIds ?? response?.InvalidSelectedSourceLocationIds
        ),
        destinationHasConflict: Boolean(response?.destinationHasConflict ?? response?.DestinationHasConflict),
        message: String(response?.message ?? response?.Message ?? ''),
      };
    } catch (_error) {
      return {
        isValid: true,
        unavailableAsDestinationLocationIds: [],
        unavailableAsSourceLocationIds: [],
        invalidSelectedSourceLocationIds: [],
        destinationHasConflict: false,
        message: '',
      };
    }
  };

  const removeUnavailableSelectedLocations = (conflictingIds: number[]) => {
    const conflictingSet = new Set(conflictingIds);
    const selectedIds = normalizeNumericLocationIds(form.getFieldValue('locationIds'));
    const remainingIds = selectedIds.filter((id) => !conflictingSet.has(id));

    form.setFieldsValue({ locationIds: remainingIds });
    setLocationPopupSelection((prev) => {
      const prevValues = Array.isArray(prev) ? prev : [];
      return prevValues.filter((id: any) => !conflictingSet.has(Number(id)));
    });
    setPopupLocationObjects((prev) => {
      const prevValues = Array.isArray(prev) ? prev : [];
      return prevValues.filter((loc: any) => !conflictingSet.has(Number(loc?.value)));
    });
  };

  const buildLocationLabelSnapshot = (locationIds: number[]): Map<number, string> => {
    const snapshot = new Map<number, string>();

    locationIds.forEach((id) => {
      const label = locationLabelMap.get(id);
      if (label) {
        snapshot.set(id, label);
      }
    });

    locationIds.forEach((id) => {
      if (snapshot.has(id)) {
        return;
      }

      const cached = locationLabelCacheRef.current.get(id);
      if (cached) {
        snapshot.set(id, cached);
      }
    });

    // Fallback to options lists in case selected values are no longer in the primary label map.
    const optionSources = [
      unplannedLocationsToShow,
      unplannedLocationsWithSelected,
      destinationOfficeOptions,
      locations,
      popupLocationObjects,
      allLocationsFromData,
      locationsForSelectedRequestor,
    ];

    locationIds.forEach((id) => {
      if (snapshot.has(id)) {
        return;
      }

      for (const source of optionSources) {
        if (!Array.isArray(source)) {
          continue;
        }

        const match = source.find((loc: any) => Number(loc?.value) === id);
        const label = match?.label;
        if (label) {
          snapshot.set(id, label);
          break;
        }
      }
    });

    return snapshot;
  };

  const getLocationDisplayLabel = (locationId: number, labelSnapshot?: Map<number, string>): string => {
    return (
      labelSnapshot?.get(locationId) ||
      locationLabelMap.get(locationId) ||
      locationLabelCacheRef.current.get(locationId) ||
      `${locationId}`
    );
  };

  const showSelectionUpdateModal = (type: 'warning' | 'info', messageLines: string[]) => {
    const openModal = type === 'warning' ? Modal.warning : Modal.info;
    const accentColor = type === 'warning' ? '#d48806' : '#096dd9';

    openModal({
      className: 'hoo-selection-update-modal',
      icon: null,
      title: (
        <div
          style={{
            lineHeight: '22px',
            fontSize: 15,
          }}
        >
          Call Forwarding Selection Updated
        </div>
      ),
      centered: true,
      width: 840,
      closable: true,
      maskClosable: true,
      content: (
        <div style={{ marginTop: 8, width: '100%' }}>
          <div style={{ display: 'grid', gap: 6, width: '100%' }}>
            {messageLines.map((line, index) => (
              <div
                key={index}
                style={{
                  background: '#ffffff',
                  border: '1px solid #f0f0f0',
                  borderLeft: type === 'warning' ? `4px solid ${accentColor}` : undefined,
                  borderRadius: 8,
                  padding: '12px 14px',
                  color: '#262626',
                  lineHeight: 1.45,
                  width: '100%',
                  boxSizing: 'border-box',
                }}
              >
                {line}
              </div>
            ))}
          </div>
        </div>
      ),
    });
  };

  const showCallForwardingSelectionUpdateMessage = (
    removedSourceIds: number[],
    removedDestinationId?: number,
    labelSnapshot?: Map<number, string>
  ) => {
    const sourceLabels = removedSourceIds.map((id) => getLocationDisplayLabel(id, labelSnapshot));
    const destinationLabel =
      removedDestinationId && Number.isFinite(removedDestinationId)
        ? getLocationDisplayLabel(removedDestinationId, labelSnapshot)
        : undefined;

    if (sourceLabels.length === 0 && !destinationLabel) {
      return;
    }

    const messageLines: string[] = [];

    if (sourceLabels.length > 0) {
      messageLines.push(
        `Selected source office(s) were removed because they already have Active/Scheduled call forwarding in the selected date range: ${sourceLabels.join(
          ', '
        )}`
      );
    }

    if (destinationLabel) {
      messageLines.push(
        `Selected destination office was removed because it already has Active/Scheduled call forwarding in the selected date range: ${destinationLabel}`
      );
    }

    showSelectionUpdateModal('warning', messageLines);
  };

  const showInactiveOfficeSelectionUpdateMessage = (
    removedSourceIds: number[],
    removedDestinationId?: number,
    labelSnapshot?: Map<number, string>
  ) => {
    const sourceLabels = removedSourceIds.map((id) => getLocationDisplayLabel(id, labelSnapshot));
    const destinationLabel =
      removedDestinationId && Number.isFinite(removedDestinationId)
        ? getLocationDisplayLabel(removedDestinationId, labelSnapshot)
        : undefined;

    if (sourceLabels.length === 0 && !destinationLabel) {
      return;
    }

    const messageLines: string[] = [];

    if (sourceLabels.length > 0) {
      messageLines.push(
        `Selected source office has been removed as that office is not active: ${sourceLabels.join(', ')}`
      );
    }

    if (destinationLabel) {
      messageLines.push(
        `Selected destination office has been removed as that office is not active: ${destinationLabel}`
      );
    }

    showSelectionUpdateModal('warning', messageLines);
  };

  const handleSubmit = async (values) => {
    try {
      const isCallForwarding =
        hooType === 'UnPlannedHoo' &&
        typeof values?.requestType === 'string' &&
        (values.requestType.toLowerCase() === 'call forwarding only' ||
          values.requestType.toLowerCase() === 'full closure with call forwarding');

      if (isCallForwarding) {
        const selectedSourceIds = normalizeNumericLocationIds(values.locationIds);

        const destinationLocationId = Number(values.destinationLocationId);
        if (!destinationLocationId) {
          showErrorNotification('Please select a destination office.');
          return;
        }

        if (selectedSourceIds.includes(destinationLocationId)) {
          showErrorNotification('Source and destination offices cannot be the same.');
          return;
        }

        const hasDestinationInAvailableList = destinationOfficeOptions.some(
          (loc: any) => Number(loc.value) === destinationLocationId
        );
        if (!hasDestinationInAvailableList) {
          showErrorNotification(
            'Selected destination office is unavailable. Please choose another destination office.'
          );
          return;
        }

        const startDate = values.startDate ? moment(values.startDate).startOf('day') : null;
        const endDate = values.endDate ? moment(values.endDate).startOf('day') : null;

        if (!startDate || !endDate) {
          showErrorNotification('Please select start and end dates.');
          return;
        }

        const startDateStr = startDate.format('YYYY-MM-DD');
        const endDateStr = endDate.format('YYYY-MM-DD');
        const validationResult = await fetchCallForwardingValidation(
          startDateStr,
          endDateStr,
          selectedSourceIds,
          destinationLocationId
        );

        const removedSourceIds = validationResult.invalidSelectedSourceLocationIds;
        const destinationHasConflict = validationResult.destinationHasConflict;
        let removedDestinationId: number | undefined;
        const removedLocationIds = [...removedSourceIds, ...(destinationHasConflict ? [destinationLocationId] : [])];
        const labelSnapshot = buildLocationLabelSnapshot(removedLocationIds);

        if (removedSourceIds.length > 0) {
          removeUnavailableSelectedLocations(removedSourceIds);
        }

        if (destinationHasConflict) {
          form.setFieldsValue({ destinationLocationId: undefined });
          removedDestinationId = destinationLocationId;
        }

        if (removedSourceIds.length > 0 || destinationHasConflict) {
          showCallForwardingSelectionUpdateMessage(removedSourceIds, removedDestinationId, labelSnapshot);
          return;
        }

        const dateCursor = startDate.clone();
        while (dateCursor.isSameOrBefore(endDate, 'day')) {
          const targetDate = dateCursor.format('YYYY-MM-DD');
          const closedLocations: any = await api.get(`/schedule-change-requests/closed-locations?date=${targetDate}`);
          const closedList = Array.isArray(closedLocations) ? closedLocations : [];

          const isDestinationClosed = closedList.some((loc: any) => {
            const id = Number(loc?.locationId ?? loc?.LocationId ?? loc?.id);
            return id === destinationLocationId;
          });

          if (isDestinationClosed) {
            showErrorNotification(
              'Selected destination office is unavailable during the forwarding period. Please select another destination office.'
            );
            return;
          }

          dateCursor.add(1, 'day');
        }
      }

      // For Standard HOO: at least one day must have hours
      // (Unplanned HOO validation is handled by form rules on each day field)
      if (hooType !== 'UnPlannedHoo') {
        if (
          values &&
          !(
            values.monday ||
            values.tuesday ||
            values.wednesday ||
            values.thursday ||
            values.friday ||
            values.saturday ||
            values.sunday
          )
        ) {
          showErrorNotification('Please select hours for at least one day!');
          return;
        }
      }
      if (onSubmit) {
        const isSingleTimeSubmit = hooType === 'UnPlannedHoo' && isSingleTimeRequestType(values?.requestType);
        const isDelayedOpeningSubmit = isDelayedOpeningRequestType(values?.requestType);
        const details = dayFields
          .map((dayKey) => {
            const value = values[dayKey.toLowerCase()];
            if (!value) return null; // Skip if undefined or null
            else if (value.toUpperCase() === 'CLOSED')
              return {
                DayOfWeek: dayKey.charAt(0).toUpperCase() + dayKey.slice(1),
                OpenTime: '00:00',
                CloseTime: '00:00',
              };
            else if (value === 'Standard HOO')
              return {
                DayOfWeek: dayKey.charAt(0).toUpperCase() + dayKey.slice(1),
                OpenTime: '00:00',
                CloseTime: '00:00',
              };

            // Only the selected time is sent; the API derives the other time from the office schedule.
            if (isSingleTimeSubmit) {
              const selectedTime = convertTo24Hour(value);
              return {
                DayOfWeek: dayKey.charAt(0).toUpperCase() + dayKey.slice(1),
                OpenTime: isDelayedOpeningSubmit ? selectedTime : '',
                CloseTime: isDelayedOpeningSubmit ? '' : selectedTime,
              };
            }

            const [openStr, closeStr] = value.split('-');
            return {
              DayOfWeek: dayKey.charAt(0).toUpperCase() + dayKey.slice(1),
              OpenTime: convertTo24Hour(openStr),
              CloseTime: convertTo24Hour(closeStr),
            };
          })
          .filter(Boolean); // Remove nulls

        const payload: any = {
          ScheduleChangeRequestId: modalData?.ScheduleChangeRequestId || null,
          RequestorId: values.requestorId,
          LocationIds: Array.isArray(values.locationIds)
            ? values.locationIds
            : values.locationIds
            ? [values.locationIds]
            : [],
          EffectiveDate:
            hooType === 'UnPlannedHoo'
              ? values.startDate
                ? values.startDate.toISOString().split('T')[0]
                : null
              : values.effectiveDate
              ? values.effectiveDate.toISOString().split('T')[0]
              : null,
          EndDate: values.endDate ? values.endDate.toISOString().split('T')[0] : null,
          RequestType: values.requestType || null,
          Reason: values.reason,
          Notes: values.notes,
          AdditionalNotes: isCallForwarding ? values.additionalNotes || null : null,
          Details: details,
          ScheduleType: hooType,
        };

        if (
          hooType === 'UnPlannedHoo' &&
          typeof values?.requestType === 'string' &&
          (values.requestType.toLowerCase() === 'call forwarding only' ||
            values.requestType.toLowerCase() === 'full closure with call forwarding')
        ) {
          const destinationLocationId = Number(values.destinationLocationId);
          payload.DestinationLocationId = destinationLocationId || null;
          const destinationLabel =
            destinationOfficeOptions.find((loc: any) => Number(loc.value) === destinationLocationId)?.label || '';
          const destinationOfficeNumber = destinationLabel.split(' - ')[0] || destinationLabel;
          payload.Notes = destinationOfficeNumber || null;

          // For both "Call Forwarding" and "Full Closure with Call Forwarding", set all visible days as closed
          payload.Details = visibleDays.map((dayKey) => ({
            DayOfWeek: dayKey,
            OpenTime: '00:00',
            CloseTime: '00:00',
          }));
        }

        dispatch(
          SaveHooAccommodation({
            ...payload,
            onSuccess: () => {
              handleFormClose(true);
            },
          })
        );
      }
    } catch (error) {
      console.error('Error fetching current pattern:', error);
    }
  };

  useEffect(() => {
    if (open) {
      dispatch(fetchRequestors(hooType === 'UnPlannedHoo' ? 'AV,RM,DM,AM' : 'RD,RCD,RCFD,AV,RM'));
      dispatch(fetchRequestReasons(hooType));
      if (hooType === 'UnPlannedHoo') {
        dispatch(fetchRequestTypes(hooType));
      }
    }
  }, [open, dispatch, hooType]);

  useEffect(() => {
    if (open && modalData) {
      const initialValues: any = {
        scheduleChangeRequestId: modalData.ScheduleChangeRequestId || undefined,
        requestorId: modalData.RequestorId || undefined,
        locationIds:
          hooType === 'UnPlannedHoo'
            ? modalData.LocationId
              ? [modalData.LocationId]
              : []
            : modalData.LocationId || undefined,
        effectiveDate: modalData.EffectiveDate ? moment(modalData.EffectiveDate) : undefined,
        reason: modalData.Reason || undefined,
        notes: modalData.Notes || '',
        additionalNotes: modalData.AdditionalNotes || '',
        doctorTemplatePatternImpact: true,
        monday: modalData.Monday || undefined,
        tuesday: modalData.Tuesday || undefined,
        wednesday: modalData.Wednesday || undefined,
        thursday: modalData.Thursday || undefined,
        friday: modalData.Friday || undefined,
        saturday: modalData.Saturday || undefined,
        sunday: modalData.Sunday || undefined,
      };
      if (hooType === 'UnPlannedHoo') {
        // Do not prefill start date for new UnPlanned HOO; only use existing modalData when editing
        initialValues.startDate = modalData?.EffectiveDate ? moment(modalData.EffectiveDate) : undefined;
        initialValues.endDate = modalData?.EndDate ? moment(modalData.EndDate) : undefined;
        initialValues.requestType = modalData?.RequestType || undefined;
      }
      setTemplateImpactChecked(true);
      form.setFieldsValue(initialValues);
    } else {
      form.resetFields();
      // Do not auto-select current date for UnPlanned HOO; leave startDate empty for user to pick
    }
  }, [open, modalData, hooType]);

  useEffect(() => {
    // For UnPlannedHoo: only prepopulate requestor if logged-in user is DM or AM
    // For StandardHoo: prepopulate for all users
    const userIsDMorAM = Array.isArray(userPositions) && (userPositions.includes('DM') || userPositions.includes('AM'));
    const shouldPrepopulateRequestor = hooType === 'UnPlannedHoo' ? userIsDMorAM : true;

    if (open && userEmailAddress && requestors.length > 0 && modalData == null && shouldPrepopulateRequestor) {
      const matchedRequestor = requestors.find(
        (r) => r.contact?.ContactEmail?.toLowerCase() === userEmailAddress.toLowerCase()
      );
      const currentRequestorId = form.getFieldValue('requestorId');
      if (matchedRequestor && currentRequestorId == null) {
        form.setFieldsValue({ requestorId: matchedRequestor.value });
        setSelectedRequestorId(matchedRequestor.value);
        dispatch(
          fetchLocationsForRequestor(
            matchedRequestor.value,
            allRequestorData,
            shouldShowAllLocations(matchedRequestor.value)
          )
        );
      } else if (currentRequestorId != null) {
        // Requestor already chosen: keep it, only refresh its locations against the latest data
        dispatch(
          fetchLocationsForRequestor(currentRequestorId, allRequestorData, shouldShowAllLocations(currentRequestorId))
        );
      }
    } else if (open && modalData != null) {
      setSelectedRequestorId(modalData.RequestorId);
      dispatch(
        fetchLocationsForRequestor(
          modalData.RequestorId,
          allRequestorData,
          shouldShowAllLocations(modalData.RequestorId)
        )
      );
    }
  }, [open, userEmailAddress, requestors, modalData, hooType, allRequestorData, userPositions]);

  const handleRequestorChange = (contactSid: number) => {
    form.setFieldsValue({ locationIds: hooType === 'UnPlannedHoo' ? [] : undefined });
    if (hooType === 'UnPlannedHoo') {
      // Offices differ per requestor, so drop any previous source/destination selection
      form.setFieldsValue({ destinationLocationId: undefined });
      setLocationPopupSelection([]);
      setPopupLocationObjects([]);
    }
    setSelectedRequestorId(contactSid);
    dispatch(fetchLocationsForRequestor(contactSid, allRequestorData, shouldShowAllLocations(contactSid)));
  };

  // Location popup state (kept at parent for form integration)
  const [locationPopupVisible, setLocationPopupVisible] = React.useState(false);
  const [locationPopupSelection, setLocationPopupSelection] = React.useState<any[]>([]);
  // Store location objects with labels from the popup
  const [popupLocationObjects, setPopupLocationObjects] = React.useState<{ value: any; label: string }[]>([]);

  const openLocationPopup = () => {
    // For UnPlanned HOO we should NOT prefill the popup with existing selections.
    if (hooType === 'UnPlannedHoo') {
      setLocationPopupSelection([]);
    } else {
      const current = form.getFieldValue('locationIds') || [];
      setLocationPopupSelection(current);
    }
    setLocationPopupVisible(true);
  };

  const handleLocationPopupOk = (vals: any[], locationObjects?: { value: any; label: string }[]) => {
    const values = vals || locationPopupSelection || [];
    // Convert to numbers to match the dropdown option values
    const numericValues = values.map((v) => (typeof v === 'number' ? v : Number(v) || v));
    setLocationPopupSelection(numericValues);
    // Store the location objects with labels
    if (locationObjects) {
      setPopupLocationObjects(locationObjects);
      rememberLocationLabels(locationObjects);
    }
    form.setFieldsValue({ locationIds: numericValues });
    setLocationPopupVisible(false);
  };

  const handleLocationPopupCancel = () => {
    setLocationPopupVisible(false);
  };

  const requestType = Form.useWatch('requestType', form);
  const isCallForwardingRequest =
    typeof requestType === 'string' &&
    (requestType.toLowerCase() === 'call forwarding only' ||
      requestType.toLowerCase() === 'full closure with call forwarding');
  const isSingleTimeRequest = hooType === 'UnPlannedHoo' && isSingleTimeRequestType(requestType);
  const singleTimeLabel = isDelayedOpeningRequestType(requestType) ? 'Opening' : 'Closing';
  const selectedSourceLocations = Form.useWatch('locationIds', form);
  const selectedDestinationLocationId = Form.useWatch('destinationLocationId', form);
  const selectedSourceLocationList = React.useMemo(() => {
    if (Array.isArray(selectedSourceLocations)) {
      return selectedSourceLocations;
    }

    if (selectedSourceLocations === null || selectedSourceLocations === undefined || selectedSourceLocations === '') {
      return [];
    }

    return [selectedSourceLocations];
  }, [selectedSourceLocations]);

  const visibleDays = React.useMemo(() => {
    if (hooType !== 'UnPlannedHoo') return dayFields;
    if (!startDateValue) return dayFields;
    const start = moment(startDateValue).startOf('day');

    // If end date not provided, show the full week (Monday-Sunday) for the selected start date
    if (!endDateValue) {
      return dayFields;
    }

    const end = moment(endDateValue).startOf('day');
    const diffDays = end.diff(start, 'days') + 1;

    // If the range covers more than a week, show all days (Mon-Sun)
    if (diffDays > 7) return dayFields;

    const days: string[] = [];
    let cursor = start.clone();
    while (cursor.isSameOrBefore(end)) {
      const d = cursor.format('dddd');
      if (!days.includes(d)) days.push(d);
      cursor.add(1, 'day');
      if (days.length > 7) break; // safety
    }
    return days;
  }, [hooType, startDateValue, endDateValue]);

  // When visibleDays change (e.g. date range modified), clear any hidden day selections
  // and auto-set visible days to CLOSED when request type is Full Day Closure with NO Call Forwarding
  useEffect(() => {
    const hiddenDays = dayFields.filter((d) => !visibleDays.includes(d)).map((d) => d.toLowerCase());
    const updates: any = {};

    // Clear hidden days
    hiddenDays.forEach((name) => {
      updates[name] = undefined;
    });

    // If Full Day Closure, set all visible days to CLOSED
    if (hooType === 'UnPlannedHoo' && requestType === 'Full Day Closure with NO Call Forwarding') {
      visibleDays.forEach((d) => {
        updates[d.toLowerCase()] = 'CLOSED';
      });
    }

    if (Object.keys(updates).length > 0) {
      form.setFieldsValue(updates);
    }
  }, [visibleDays, form, hooType, requestType]);

  // When request type changes to Full Day Closure with NO Call Forwarding, override all visible day selections to CLOSED
  // When switching away from Full Day Closure with NO Call Forwarding, clear all day selections
  useEffect(() => {
    if (hooType === 'UnPlannedHoo') {
      const updates: any = {};
      if (requestType === 'Full Day Closure with NO Call Forwarding') {
        visibleDays.forEach((d) => {
          updates[d.toLowerCase()] = 'CLOSED';
        });
        // Also clear any hidden days
        dayFields
          .filter((d) => !visibleDays.includes(d))
          .forEach((d) => {
            updates[d.toLowerCase()] = undefined;
          });
      } else if (requestType === 'Revert to Standard HOO') {
        // Set all visible days to Standard HOO
        visibleDays.forEach((d) => {
          updates[d.toLowerCase()] = 'Standard HOO';
        });
        // Clear any hidden days
        dayFields
          .filter((d) => !visibleDays.includes(d))
          .forEach((d) => {
            updates[d.toLowerCase()] = undefined;
          });
      } else if (isCallForwardingRequest) {
        // For call forwarding, proposed hours are hidden but backend still requires details.
        // Populate visible days as CLOSED so submit payload remains valid.
        visibleDays.forEach((d) => {
          updates[d.toLowerCase()] = 'CLOSED';
        });
        dayFields
          .filter((d) => !visibleDays.includes(d))
          .forEach((d) => {
            updates[d.toLowerCase()] = undefined;
          });
      } else if (requestType) {
        // Switching away from Full Day Closure with NO Call Forwarding or Revert to Standard HOO — clear all day selections
        dayFields.forEach((d) => {
          updates[d.toLowerCase()] = undefined;
        });
      }

      if (!isCallForwardingRequest) {
        updates.additionalNotes = undefined;
        updates.destinationLocationId = undefined;
      }

      if (Object.keys(updates).length > 0) {
        form.setFieldsValue(updates);
      }
    }
  }, [requestType, isCallForwardingRequest, hooType, visibleDays, form]);

  useEffect(() => {
    if (hooType !== 'UnPlannedHoo') {
      return;
    }

    const currentLocationIds = form.getFieldValue('locationIds');

    if (isCallForwardingRequest) {
      if (Array.isArray(currentLocationIds)) {
        const firstSelectedLocation = currentLocationIds.length > 0 ? currentLocationIds[0] : undefined;
        form.setFieldsValue({ locationIds: firstSelectedLocation });
      }
      return;
    }

    if (
      currentLocationIds !== null &&
      currentLocationIds !== undefined &&
      currentLocationIds !== '' &&
      !Array.isArray(currentLocationIds)
    ) {
      form.setFieldsValue({ locationIds: [currentLocationIds] });
    }
  }, [isCallForwardingRequest, hooType, form]);

  const unplannedLocations = useSelector(getUnplannedLocations);

  const allLocationsFromData = React.useMemo(() => {
    if (!allRequestorData || allRequestorData.length === 0) return [];
    const map = new Map();
    allRequestorData.forEach((item: any) => {
      if (item.LocationSid && !map.has(item.LocationSid)) {
        map.set(item.LocationSid, { label: `${item.LocationNumber} - ${item.LocationName}`, value: item.LocationSid });
      }
    });
    return Array.from(map.values());
  }, [allRequestorData]);

  // For UnPlanned HOO: compute locations filtered by selected requestor
  // This is used when RVP/Admin selects a different requestor to show only that person's locations
  const locationsForSelectedRequestor = React.useMemo(() => {
    if (!selectedRequestorId || !allRequestorData || allRequestorData.length === 0) return [];
    const map = new Map();
    allRequestorData
      .filter((item: any) => item.ContactSid === selectedRequestorId)
      .forEach((item: any) => {
        if (item.LocationSid && !map.has(item.LocationSid)) {
          map.set(item.LocationSid, {
            label: `${item.LocationNumber} - ${item.LocationName}`,
            value: item.LocationSid,
          });
        }
      });
    return Array.from(map.values());
  }, [allRequestorData, selectedRequestorId]);

  // Determine which locations to show for UnPlanned HOO:
  // - If selected requestor is DM or AM → show filtered locations for that requestor
  // - For all other requestors (AV, RM, etc.) → show all locations
  // This applies to all request types including Call Forwarding and Full Closure with Call Forwarding
  const unplannedLocationsToShow = React.useMemo(() => {
    if (hooType !== 'UnPlannedHoo') return [];

    // DM acting on behalf of another DM: restrict to the selected DM's offices
    if (isActingOnBehalfOfAnotherDM) {
      return locationsForSelectedRequestor;
    }

    // Check if selected requestor is DM or AM
    if (selectedRequestorId) {
      const requestorData = allRequestorData.find((item: any) => item.ContactSid === selectedRequestorId);
      if (requestorData) {
        const position = requestorData.Position || requestorData.JobPosition || '';
        // If requestor is DM or AM, show filtered locations for that requestor
        if (['DM', 'AM'].includes(position) && locationsForSelectedRequestor.length > 0) {
          return locationsForSelectedRequestor;
        }
      }
    }

    // For all other requestors (AV, RM, etc.) or no requestor selected: show all locations
    if (unplannedLocations && unplannedLocations.length > 0) {
      return unplannedLocations;
    }

    return allLocationsFromData || [];
  }, [
    hooType,
    isActingOnBehalfOfAnotherDM,
    selectedRequestorId,
    allRequestorData,
    locationsForSelectedRequestor,
    unplannedLocations,
    allLocationsFromData,
  ]);

  // Create a lookup map for location labels from all available sources
  // This helps display proper labels for locations selected from the popup
  const locationLabelMap = React.useMemo(() => {
    const map = new Map<number, string>();
    // Add from popupLocationObjects (highest priority - directly from popup selection)
    (popupLocationObjects || []).forEach((loc: any) => {
      if (loc.value != null && loc.label) {
        map.set(Number(loc.value), loc.label);
      }
    });
    // Add from unplannedLocations
    (unplannedLocations || []).forEach((loc: any) => {
      if (loc.value != null && loc.label && !map.has(Number(loc.value))) {
        map.set(Number(loc.value), loc.label);
      }
    });
    // Add from allLocationsFromData
    (allLocationsFromData || []).forEach((loc: any) => {
      if (loc.value != null && loc.label && !map.has(Number(loc.value))) {
        map.set(Number(loc.value), loc.label);
      }
    });
    // Add from locationsForSelectedRequestor
    (locationsForSelectedRequestor || []).forEach((loc: any) => {
      if (loc.value != null && loc.label && !map.has(Number(loc.value))) {
        map.set(Number(loc.value), loc.label);
      }
    });
    return map;
  }, [popupLocationObjects, unplannedLocations, allLocationsFromData, locationsForSelectedRequestor]);

  // Merge the displayed options with any selected values that might not be in the current list
  const unplannedLocationsWithSelected = React.useMemo(() => {
    const unavailableIds = new Set(
      isCallForwardingRequest
        ? [...callForwardingUnavailableDestinationLocationIds, ...callForwardingUnavailableSourceLocationIds]
        : ([] as number[])
    );
    const currentIds = new Set(unplannedLocationsToShow.map((loc) => Number(loc.value)));
    const selectedIds = [
      ...normalizeNumericLocationIds(selectedSourceLocationList),
      ...normalizeNumericLocationIds(locationPopupSelection),
    ];
    const additional: { label: string; value: number }[] = [];

    selectedIds.forEach((id) => {
      const numId = Number(id);
      if (!currentIds.has(numId) && locationLabelMap.has(numId) && !unavailableIds.has(numId)) {
        additional.push({ label: locationLabelMap.get(numId)!, value: numId });
      }
    });

    const filteredBaseOptions = unplannedLocationsToShow.filter((loc: any) => {
      const locationId = Number(loc.value);
      return !unavailableIds.has(locationId);
    });

    return [...filteredBaseOptions, ...additional];
  }, [
    unplannedLocationsToShow,
    locationLabelMap,
    locationPopupSelection,
    isCallForwardingRequest,
    callForwardingUnavailableDestinationLocationIds,
    callForwardingUnavailableSourceLocationIds,
  ]);

  const destinationOfficeOptions = React.useMemo(() => {
    const selectedSourceIds = new Set(selectedSourceLocationList.map((id: any) => Number(id)));
    const unavailableIds = new Set([
      ...callForwardingUnavailableDestinationLocationIds,
      ...callForwardingUnavailableSourceLocationIds,
    ]);
    return unplannedLocationsToShow.filter((loc: any) => {
      const locationId = Number(loc.value);
      return !selectedSourceIds.has(locationId) && !unavailableIds.has(locationId);
    });
  }, [
    unplannedLocationsToShow,
    selectedSourceLocationList,
    callForwardingUnavailableDestinationLocationIds,
    callForwardingUnavailableSourceLocationIds,
  ]);

  useEffect(() => {
    rememberLocationLabels(popupLocationObjects as any[]);
    rememberLocationLabels(unplannedLocations as any[]);
    rememberLocationLabels(unplannedLocationsToShow as any[]);
    rememberLocationLabels(unplannedLocationsWithSelected as any[]);
    rememberLocationLabels(destinationOfficeOptions as any[]);
    rememberLocationLabels(locations as any[]);
    rememberLocationLabels(allLocationsFromData as any[]);
    rememberLocationLabels(locationsForSelectedRequestor as any[]);
  }, [
    popupLocationObjects,
    unplannedLocations,
    unplannedLocationsToShow,
    unplannedLocationsWithSelected,
    destinationOfficeOptions,
    locations,
    allLocationsFromData,
    locationsForSelectedRequestor,
  ]);

  useEffect(() => {
    if (!open || hooType !== 'UnPlannedHoo' || !isCallForwardingRequest) {
      return;
    }

    if (!Array.isArray(unplannedLocations) || unplannedLocations.length === 0) {
      return;
    }

    const activeOfficeIds = new Set(
      unplannedLocations.map((loc: any) => Number(loc?.value)).filter((id: number) => Number.isFinite(id) && id > 0)
    );

    const selectedSourceIds = normalizeNumericLocationIds(selectedSourceLocationList);
    const removedSourceIds = selectedSourceIds.filter((id) => !activeOfficeIds.has(id));

    const destinationId = Number(selectedDestinationLocationId);
    const removedDestinationId =
      Number.isFinite(destinationId) && destinationId > 0 && !activeOfficeIds.has(destinationId)
        ? destinationId
        : undefined;

    if (removedSourceIds.length === 0 && !removedDestinationId) {
      return;
    }

    const removedLocationIds = [...removedSourceIds, ...(removedDestinationId ? [removedDestinationId] : [])];
    const labelSnapshot = buildLocationLabelSnapshot(removedLocationIds);

    if (removedSourceIds.length > 0) {
      if (isCallForwardingRequest) {
        form.setFieldsValue({ locationIds: undefined });
        setLocationPopupSelection([]);
      } else {
        removeUnavailableSelectedLocations(removedSourceIds);
      }
    }

    if (removedDestinationId) {
      form.setFieldsValue({ destinationLocationId: undefined });
    }

    showInactiveOfficeSelectionUpdateMessage(removedSourceIds, removedDestinationId, labelSnapshot);
  }, [
    open,
    hooType,
    isCallForwardingRequest,
    unplannedLocations,
    selectedSourceLocationList,
    selectedDestinationLocationId,
    form,
    locationLabelMap,
    unplannedLocationsWithSelected,
  ]);

  useEffect(() => {
    if (!open || hooType !== 'UnPlannedHoo' || !isCallForwardingRequest || !startDateValue) {
      setCallForwardingUnavailableDestinationLocationIds([]);
      setCallForwardingUnavailableSourceLocationIds([]);
      return;
    }

    const startDate = moment(startDateValue).format('YYYY-MM-DD');
    const endDate = moment(endDateValue || startDateValue).format('YYYY-MM-DD');
    const selectedSourceIds = normalizeNumericLocationIds(selectedSourceLocationList);
    const destinationId = Number(selectedDestinationLocationId);
    let cancelled = false;

    const validate = async () => {
      const validationResult = await fetchCallForwardingValidation(
        startDate,
        endDate,
        selectedSourceIds,
        Number.isFinite(destinationId) && destinationId > 0 ? destinationId : undefined
      );

      if (cancelled) {
        return;
      }

      setCallForwardingUnavailableDestinationLocationIds(validationResult.unavailableAsDestinationLocationIds);
      setCallForwardingUnavailableSourceLocationIds(validationResult.unavailableAsSourceLocationIds);

      const removedSourceIds = validationResult.invalidSelectedSourceLocationIds;
      const destinationHasConflict = validationResult.destinationHasConflict;
      let removedDestinationId: number | undefined;
      const removedLocationIds = [
        ...removedSourceIds,
        ...(destinationHasConflict && Number.isFinite(destinationId) && destinationId > 0 ? [destinationId] : []),
      ];
      const labelSnapshot = buildLocationLabelSnapshot(removedLocationIds);

      if (removedSourceIds.length > 0) {
        removeUnavailableSelectedLocations(removedSourceIds);
      }

      if (destinationHasConflict && Number.isFinite(destinationId) && destinationId > 0) {
        form.setFieldsValue({ destinationLocationId: undefined });
        removedDestinationId = destinationId;
      }

      if (removedSourceIds.length > 0 || removedDestinationId) {
        showCallForwardingSelectionUpdateMessage(removedSourceIds, removedDestinationId, labelSnapshot);
      }
    };

    validate();

    return () => {
      cancelled = true;
    };
  }, [
    open,
    hooType,
    isCallForwardingRequest,
    startDateValue,
    endDateValue,
    selectedSourceLocationList,
    selectedDestinationLocationId,
    form,
    locationLabelMap,
  ]);

  useEffect(() => {
    if (open && hooType === 'UnPlannedHoo') {
      dispatch(fetchUnplannedLocations(filteredPositions, isCallForwardingRequest));
    }
  }, [open, hooType, dispatch, filteredPositions, isCallForwardingRequest]);

  return (
    <Drawer
      title={
        modalData
          ? hooType === 'UnPlannedHoo'
            ? 'Edit Temporary Hours Request'
            : 'Edit Permanent Hours Request'
          : hooType === 'UnPlannedHoo'
          ? 'Add New Temporary Hours Request'
          : 'Add New Permanent Hours Request'
      }
      closable={false}
      width={480}
      open={open}
      destroyOnClose
      footer={
        <div style={{ textAlign: 'right' }}>
          <Button
            onClick={() => {
              handleFormClose(false);
            }}
            style={{ marginRight: 8 }}
          >
            Cancel
          </Button>
          <Button
            type='primary'
            onClick={() => {
              console.log('submitting form', JSON.stringify(form.getFieldsValue()));
              form.submit();
            }}
            loading={loading}
            disabled={!loading && hooType !== 'UnPlannedHoo' && templateImpactChecked === false}
          >
            Submit
          </Button>
        </div>
      }
    >
      <Form
        form={form}
        layout='vertical'
        onFinish={handleSubmit}
        initialValues={{ locationIds: hooType === 'UnPlannedHoo' ? [] : undefined }}
      >
        <Form.Item
          label={
            hooType === 'UnPlannedHoo' ? 'Name of Requestor (RVP/AVP/DM/AM)' : 'Name of Requestor (RD/RCD/AVP/RVP)'
          }
          name='requestorId'
          rules={[{ required: true, message: 'Requestor is required' }]}
        >
          <Select
            showSearch
            disabled={
              (modalData != null && modalData.RequestorId != null) ||
              (hooType === 'UnPlannedHoo' && isDMOrAMOnly && !isDMOnly)
            }
            placeholder='Type to search requestor'
            onChange={handleRequestorChange}
            filterOption={(input, option) =>
              option?.children ? option.children.toString().toLowerCase().includes(input.toLowerCase()) : false
            }
            getPopupContainer={(trigger) => trigger.parentNode}
          >
            {requestorOptions &&
              requestorOptions.map((r) => (
                <Option key={r.value} value={r.value}>
                  {r.label}
                </Option>
              ))}
          </Select>
        </Form.Item>

        {hooType === 'UnPlannedHoo' ? (
          <>
            <Form.Item
              label={
                <span>
                  Location
                  {!modalData && !isCallForwardingRequest && (
                    <a
                      onClick={openLocationPopup}
                      style={{ marginLeft: 6, fontStyle: 'italic', cursor: 'pointer', color: '#1890ff' }}
                      role='button'
                      tabIndex={0}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') openLocationPopup();
                      }}
                    >
                      Select Locations...
                    </a>
                  )}
                </span>
              }
              name='locationIds'
              rules={[{ required: true, message: 'At least one location is required' }]}
            >
              <Select
                mode={isCallForwardingRequest ? undefined : 'multiple'}
                showSearch
                allowClear
                placeholder={isCallForwardingRequest ? 'Select source location' : 'Select location(s)'}
                disabled={false}
                onChange={(value, option: any) => {
                  const values = Array.isArray(value) ? value : value != null && value !== '' ? [value] : [];
                  setLocationPopupSelection(normalizeNumericLocationIds(values));

                  if (Array.isArray(option)) {
                    rememberLocationLabels(option);
                  } else if (option) {
                    rememberLocationLabels([option]);
                  }
                }}
                filterOption={(input, option) => {
                  const label = (option?.label ?? option?.children ?? '').toString().toLowerCase();
                  return label.includes(input.toLowerCase());
                }}
                getPopupContainer={(trigger) => trigger.parentNode}
                maxTagCount={1}
                maxTagPlaceholder={(omitted) => `+${omitted.length}`}
                optionLabelProp='children'
              >
                {unplannedLocationsWithSelected.map((loc) => (
                  <Option key={loc.value} value={loc.value} label={loc.label}>
                    {loc.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item label='Request Type' name='requestType' rules={[{ required: true }]}>
              <Select placeholder='Select request type' getPopupContainer={(trigger) => trigger.parentNode}>
                {requestTypes &&
                  requestTypes.map((opt) => (
                    <Option key={opt.value} value={opt.value}>
                      {opt.label}
                    </Option>
                  ))}
              </Select>
            </Form.Item>
            <Form.Item
              label='Start Date'
              name='startDate'
              rules={[{ required: true, message: 'Please select start date' }]}
            >
              <DatePicker
                format={'YYYY-MM-DD'}
                getPopupContainer={(trigger) => trigger.parentNode as HTMLElement}
                inputReadOnly
                disabledDate={(d) => d && d < moment().startOf('day')}
              />
            </Form.Item>
            <Form.Item
              label='End Date'
              name='endDate'
              rules={[
                { required: true, message: 'Please select end date' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const start = getFieldValue('startDate');
                    if (!value || !start) return Promise.resolve();
                    if (moment(value).isAfter(moment(start))) return Promise.resolve();
                    return Promise.reject(new Error('End Date must be later than Start Date'));
                  },
                }),
              ]}
            >
              <DatePicker
                format={'YYYY-MM-DD'}
                getPopupContainer={(trigger) => trigger.parentNode as HTMLElement}
                inputReadOnly
                disabledDate={(d) => d && d < moment().startOf('day')}
              />
            </Form.Item>

            {isCallForwardingRequest && (
              <Form.Item
                label='Destination Office'
                name='destinationLocationId'
                rules={[{ required: true, message: 'Please select destination office' }]}
              >
                <Select
                  showSearch
                  placeholder='Select destination office'
                  allowClear
                  onChange={(_value, option: any) => {
                    if (option) {
                      rememberLocationLabels([option]);
                    }
                  }}
                  filterOption={(input, option) => {
                    const label = (option?.label ?? option?.children ?? '').toString().toLowerCase();
                    return label.includes(input.toLowerCase());
                  }}
                  getPopupContainer={(trigger) => trigger.parentNode}
                >
                  {destinationOfficeOptions.map((loc: any) => (
                    <Option key={loc.value} value={loc.value} label={loc.label}>
                      {loc.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            )}
          </>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Form.Item
              label='Location'
              name='locationIds'
              rules={[{ required: true, message: 'Location is required' }]}
              style={{ flex: 1, marginBottom: 0 }}
            >
              <Select
                allowClear
                showSearch
                placeholder='Select location'
                disabled={!form.getFieldValue('requestorId') || (modalData != null && modalData.LocationId != null)}
                filterOption={(input, option) => {
                  const label = (option?.label ?? option?.children ?? '').toString().toLowerCase();
                  return label.includes(input.toLowerCase());
                }}
                getPopupContainer={(trigger) => trigger.parentNode}
                style={{ width: '100%' }}
              >
                {(() => {
                  // Ensure when editing an existing Standard HOO the current location is present in options
                  if (locations && locations.length > 0) {
                    return locations.map((loc) => (
                      <Option key={loc.value} value={loc.value}>
                        {loc.label}
                      </Option>
                    ));
                  }
                  return null;
                })()}
              </Select>
            </Form.Item>
            {hooType !== 'StandardHoo' && (
              <a
                style={{ cursor: 'pointer', color: '#1890ff' }}
                onClick={() => {
                  form.setFieldsValue({ locationIds: [] });
                }}
              >
                Clear
              </a>
            )}
          </div>
        )}

        {visibleDays &&
          !isCallForwardingRequest &&
          visibleDays.map((opt) => (
            <Form.Item
              key={`ele-${opt}`}
              label={isSingleTimeRequest ? `Proposed New ${opt} ${singleTimeLabel} Time` : `Proposed New ${opt} Hours`}
              name={opt.toLowerCase()}
              rules={
                hooType === 'UnPlannedHoo'
                  ? [
                      {
                        required: true,
                        message: isSingleTimeRequest
                          ? `Please select ${singleTimeLabel.toLowerCase()} time for ${opt}`
                          : `Please select hours for ${opt}`,
                      },
                    ]
                  : []
              }
            >
              <Select
                placeholder={isSingleTimeRequest ? `Select ${singleTimeLabel.toLowerCase()} time` : 'Select hours'}
                allowClear
                showSearch
                filterOption={(input, option) => {
                  const label = (option?.label ?? option?.children ?? '').toString().toLowerCase();
                  return label.includes(input.toLowerCase());
                }}
                getPopupContainer={(trigger) => trigger.parentNode}
              >
                {(isSingleTimeRequest ? singleTimeOptions : hourOptions || []).map((opt) => (
                  <Option key={opt} value={opt}>
                    {opt}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          ))}

        {hooType !== 'UnPlannedHoo' && (
          <Form.Item
            label='Doctor Template Pattern Impact'
            name='doctorTemplatePatternImpact'
            valuePropName='checked'
            style={{ display: 'flex', alignItems: 'center' }}
            labelCol={{ span: 24 }}
            wrapperCol={{ span: 24 }}
            rules={[{ required: true, message: 'Please accept the doctor template pattern impact' }]}
          >
            <Checkbox
              onChange={(e) => {
                setTemplateImpactChecked(e.target.checked);
              }}
            />
          </Form.Item>
        )}

        {hooType !== 'UnPlannedHoo' && (
          <Form.Item
            label='Requested Effective Date for Change'
            name='effectiveDate'
            rules={[{ required: true, message: 'Please select start date' }]}
          >
            <DatePicker
              format={'YYYY-MM-DD'}
              getPopupContainer={(trigger) => trigger.parentNode as HTMLElement}
              disabledDate={(d) => {
                const dayAfterTomorrow = moment().add(2, 'days').startOf('day');
                return d && d < dayAfterTomorrow;
              }}
              inputReadOnly
            />
          </Form.Item>
        )}
        <Form.Item
          label='Request Reason'
          name='reason'
          rules={[{ required: true, message: 'Request reason is required' }]}
        >
          <Select
            placeholder='Select request reason'
            allowClear
            showSearch
            filterOption={(input, option) => {
              const label = (option?.label ?? option?.children ?? '').toString().toLowerCase();
              return label.includes(input.toLowerCase());
            }}
            getPopupContainer={(trigger) => trigger.parentNode}
          >
            {requestReasons &&
              requestReasons.map((opt) => (
                <Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Option>
              ))}
          </Select>
        </Form.Item>
        {!isCallForwardingRequest && (
          <Form.Item
            label='Additional Notes'
            name='notes'
            rules={[
              {
                validator: (_, value) => {
                  const reason = form.getFieldValue('reason')?.toLowerCase() || '';
                  if (reason.includes('other') && (!value || !value.trim())) {
                    return Promise.reject(new Error('Please enter notes if "Other" is selected'));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <TextArea rows={3} maxLength={4000} />
          </Form.Item>
        )}
        {isCallForwardingRequest && (
          <Form.Item label='Additional Notes' name='additionalNotes'>
            <TextArea rows={3} maxLength={4000} />
          </Form.Item>
        )}
      </Form>
      <LocationPickerPopup
        visible={locationPopupVisible}
        initialSelected={locationPopupSelection}
        allLocationsFromData={allLocationsFromData}
        allRequestorData={allRequestorData}
        onOk={handleLocationPopupOk}
        onCancel={handleLocationPopupCancel}
        positions={filteredPositions}
        selectedRequestorId={!isDMOrAMOnly || isActingOnBehalfOfAnotherDM ? selectedRequestorId : null}
        showAllLocations={selectedRequestorId ? shouldShowAllLocations(selectedRequestorId) : false}
      />
    </Drawer>
  );
};

export default AddHooRequestDrawer;
